import React, { useState } from "react";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { FIT_QUESTIONS } from "@/data";
import { useEnquiryForm } from "@/context/FormContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { Gauge, ArrowRight, RotateCcw } from "lucide-react";

function classify(a) {
  const stage = a.business_stage;
  if (["Manufacturing preparation", "Commercial manufacturing"].includes(stage)) return "Manufacturing Assessment Required";
  if (["Idea", "Research"].includes(stage)) return "Early-Stage Validation Fit";
  if (["Prototype", "Proof of concept"].includes(stage)) return "Prototype and Product Support Fit";
  if (["Pilot", "Pre-revenue"].includes(stage)) return "Commercialisation Support Fit";
  if (["Revenue generating", "Growth stage"].includes(stage)) return "Growth-Capital Readiness";
  return "Further Information Required";
}

const field = "bg-[#0C0C10] border-white/10 text-[#F4F4F5] h-11";

export default function FitChecker() {
  const { openForm } = useEnquiryForm();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const answeredAll = FIT_QUESTIONS.every((q) => answers[q.key]);

  const run = () => setResult(classify(answers));
  const reset = () => { setAnswers({}); setResult(null); };
  const continueToForm = () => openForm("founder", {
    business_stage: answers.business_stage,
    revenue: answers.revenue,
    amount_sought: answers.amount_sought,
    use_of_funds: answers.use_of_funds,
    support: answers.support ? [answers.support] : [],
    description: `Sector: ${answers.sector || "-"}. Preliminary fit: ${result}.`,
  });

  return (
    <section id="fit" className="relative py-24 md:py-36 border-t border-white/5 overflow-hidden" data-testid="fit-checker-section">
      <div className="absolute left-0 top-1/3 w-[500px] h-[500px] glow-blue opacity-50" />
      <div className="max-w-5xl mx-auto px-6 md:px-12 relative">
        <Reveal>
          <div className="flex items-center gap-3 mb-4"><Gauge className="h-5 w-5 text-[#00D1FF]" /><span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00D1FF]">Funding Fit Checker</span></div>
          <SectionHeading title="Check your FundAI fit." />
          <p className="mt-6 max-w-2xl text-base md:text-lg font-light text-zinc-400 leading-relaxed">A quick, no-login preliminary classification. Answer six questions to see where your business fits in our journey.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 rounded-2xl border border-white/10 bg-[#0A0A0E] p-6 md:p-10">
            <div className="grid md:grid-cols-2 gap-5">
              {FIT_QUESTIONS.map((q) => (
                <div key={q.key}>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{q.label}</label>
                  <Select value={answers[q.key] || ""} onValueChange={(v) => set(q.key, v)}>
                    <SelectTrigger className={field} data-testid={`fit-${q.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5] max-h-64">
                      {q.options.map((o) => <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-white">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <AnimatePresence>
              {result && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="mt-8 rounded-xl border border-[#0057FF]/30 bg-[#0057FF]/5 p-6" data-testid="fit-result">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Preliminary classification</p>
                    <p className="font-head text-2xl md:text-3xl font-medium text-metallic mt-2">{result}</p>
                    <p className="mt-4 text-xs text-zinc-500 leading-relaxed">This result is a preliminary classification only. It is not a funding decision, sanction, term sheet, investment offer, loan approval or commitment.</p>
                    <button onClick={continueToForm} className="group mt-6 inline-flex items-center gap-2 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-6 py-3 font-medium transition-colors active:scale-[0.98]" data-testid="fit-continue-btn">
                      Continue to Enquiry Form <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-8 flex flex-wrap gap-3">
              {!result ? (
                <button onClick={run} disabled={!answeredAll} className="inline-flex items-center gap-2 bg-[#0057FF] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0047D6] text-white rounded-full px-7 py-3 font-medium transition-colors active:scale-[0.98]" data-testid="fit-check-btn">
                  Check my fit
                </button>
              ) : (
                <button onClick={reset} className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white rounded-full px-6 py-3 font-medium transition-colors" data-testid="fit-reset-btn">
                  <RotateCcw className="h-4 w-4" /> Start over
                </button>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
